import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Ruler } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Select } from '@/Components/ui/Select';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { StatusBadge } from '@/Components/ui/Badge';
import { TableActions } from '@/Components/ui/TableActions';
import unidadeMedidaService, { type UnidadeMedida } from '@/services/unidadeMedida';

export default function UnidadesMedida() {
  const navigate = useNavigate();
  const [items, setItems] = useState<UnidadeMedida[]>([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState<string>('');

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await unidadeMedidaService.listar();
      setItems(data);
    } catch (error) {
      console.error('Erro ao carregar unidades', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const itensFiltrados = useMemo(() => {
    let resultado = items;
    if (busca) {
      const termo = busca.toLowerCase();
      resultado = resultado.filter(
        (item) => item.codigo?.toLowerCase().includes(termo) || item.descricao?.toLowerCase().includes(termo)
      );
    }
    if (filtroAtivo !== '') {
      const bool = filtroAtivo === 'true';
      resultado = resultado.filter((item) => item.ativo === bool);
    }
    return resultado;
  }, [items, busca, filtroAtivo]);

  const deletar = async (item: UnidadeMedida) => {
    try {
      await unidadeMedidaService.excluir(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao excluir unidade', error);
      throw error;
    }
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<Ruler size={21} strokeWidth={2} />}
        title="Unidades de Medida"
        description="Cadastro das unidades de medida utilizadas pelos produtos."
        actions={
          <Button type="button" onClick={() => navigate('/producao/unidades-medida/novo')}>
            <Plus size={16} strokeWidth={2.2} />
            Nova Unidade
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions">
              <div className="nx-search-field">
                <Search size={15} strokeWidth={2} aria-hidden />
                <input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar unidade..."
                />
              </div>
              <Select filter value={filtroAtivo} onChange={(e) => setFiltroAtivo(e.target.value)}>
                <option value="">Todos</option>
                <option value="true">Ativos</option>
                <option value="false">Inativos</option>
              </Select>
            </div>
          }
        />

        {!loading && itensFiltrados.length === 0 ? (
          <EmptyState message="Nenhuma unidade encontrada." />
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descrição</th>
                  <th>Status</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  itensFiltrados.map((item) => (
                    <tr key={item.id}>
                      <td>{item.codigo}</td>
                      <td>{item.descricao}</td>
                      <td>
                        <StatusBadge label={item.ativo ? 'Ativo' : 'Inativo'} tone={item.ativo ? 'success' : 'danger'} />
                      </td>
                      <td className="nx-col-acoes">
                        <TableActions
                          onEdit={() => navigate(`/producao/unidades-medida/${item.id}/editar`)}
                          onDelete={() => deletar(item)}
                          deleteTitle="Excluir unidade"
                          deleteDescription={`Tem certeza de que deseja excluir a unidade "${item.codigo}"? Esta ação não poderá ser desfeita.`}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
